// src/components/common/Buttons.tsx
import React from 'react';
import {
  Box,
  Button,
  ButtonProps,
  CircularProgress,
  Fab,
  FabProps,
  IconButton,
  IconButtonProps,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Cancel,
  CheckCircle,
  Delete,
  Save,
} from '@mui/icons-material';
import { colors } from '../../theme/theme';
import {
  getAccessibilityButtonHoverExtra,
  ButtonAccessibilityHoverProps,
} from '../forms/shared/AccessibilityHoverStyles';
import { useAccessibility } from '../../contexts/AccessibilityContext';
import {
  generateFABPositioning,
  FABStyles,
} from '../forms/shared/MediaQueryUtils';
import { alpha } from '@mui/material/styles';

// Base interface for all custom button props
interface BaseButtonProps extends Omit<ButtonProps, 'minWidth'> {
  padding?: string;
  minWidth?: string;
  isActive?: boolean;
}

// Enhanced BackButton props
interface BackButtonProps extends Omit<BaseButtonProps, 'onClick'> {
  // Navigation options
  navigationMode?: 'browser' | 'custom';
  customPath?: string;
  steps?: number; // How many steps to go back in browser history
  // Custom onClick override (if provided, it will override navigation)
  onClick?: () => void;
  // Accessibility
  'aria-label'?: string;
}

// Simplified FAB config interface - uses granular breakpoints from MediaQueryUtils
interface FABPositioningConfig {
  mobile?: FABStyles;
  tablet?: FABStyles;
  desktop?: FABStyles;
  large?: FABStyles;
  xlarge?: FABStyles;
  xxlarge?: FABStyles;
}

// Form button props for Cancel and Save buttons
interface FormActionButtonProps
  extends
    Omit<BaseButtonProps, 'children' | 'startIcon'>,
    ButtonAccessibilityHoverProps {
  isSubmitting?: boolean;
  text?: string;
}

// Enhanced FAB interface extending MUI FabProps with responsive positioning
interface StyledFabProps extends Omit<FabProps, 'color'> {
  position?: 'fixed' | 'absolute' | 'relative';
  bottom?: string;
  right?: string;
  top?: string;
  left?: string;
  // Icon/text functionality
  icon?: React.ReactNode;
  hoverText?: string;
  showIconByDefault?: boolean;
  // Icon-to-icon transitions
  hoverIcon?: React.ReactNode;
  // Responsive positioning - uses granular breakpoints
  fabPositioning?: FABPositioningConfig;
  // Position mode control
  positionRelative?: boolean;
  // Icon scaling - scales only the icon, not the FAB or positioning
  iconScale?: number;
}

// SignInButton - For login page authentication
interface SignInButtonProps extends Omit<BaseButtonProps, 'children'> {
  isLoading?: boolean;
  text?: string;
}

interface StyledIconButtonProps extends Omit<IconButtonProps, 'color'> {
  variant?: 'standard' | 'bold' | 'delete';
  // Flat props for custom styling - override variant when provided
  backgroundColor?: string;
  color?: string;
  hoverBackgroundColor?: string;
  hoverColor?: string;
}

// Base button styles that all buttons will share
const baseButtonStyles = {
  fontSize: '1.5rem',
  fontWeight: 600,
  padding: '0.75rem 1.25rem',
  borderRadius: '0',
  textTransform: 'uppercase' as const,
  letterSpacing: 'normal',
  transition: 'letter-spacing 0.3s ease',
  border: '2px solid transparent',
  whiteSpace: 'nowrap' as const,
  overflow: 'hidden',
  textAlign: 'center' as const,

  '&:hover': {
    transform: 'none !important',
    boxShadow: 'none',
    letterSpacing: '3px',
  },

  '&:focus': {
    outline: `3px solid ${colors.accessibility.focusOutline || '#f39c12'}`,
    outlineOffset: '2px',
  },

  '&.Mui-disabled': {
    backgroundColor: colors.grey[500],
    color: colors.grey[600],
    borderColor: 'transparent',
    fontStyle: 'italic',
    cursor: 'not-allowed',
    '&:hover': {
      letterSpacing: 'normal',
    },
  },
};

export const SignInButton: React.FC<SignInButtonProps> = ({
  isLoading = false,
  text,
  type = 'submit',
  disabled,
  ...props
}) => {
  const buttonText = text || (isLoading ? 'Signing In...' : 'Sign In');

  return (
    <FormButton
      type={type}
      variant="contained"
      disabled={disabled || isLoading}
      startIcon={
        isLoading ? <CircularProgress size={20} color="inherit" /> : undefined
      }
      sx={{
        minWidth: '100%',
        minHeight: '48px',
        mt: 3,
        mb: 2,
        backgroundColor: colors.primary.main,
        color: 'white',
        fontSize: '1.3rem',
        fontWeight: 700,
        borderRadius: '0',
        '&:hover': {
          backgroundColor: colors.primary.dark,
          letterSpacing: '3px',
        },
        '&:disabled': {
          backgroundColor: colors.tertiary.light,
          color: 'white',
        },
        ...props.sx,
      }}
      {...props}
    >
      {buttonText}
    </FormButton>
  );
};

// BeingMenuButton - For actions in being (client/pet) menus
export const BeingMenuButton = styled(Button, {
  shouldForwardProp: (prop) =>
    prop !== 'minWidth' && prop !== 'isActive' && prop !== 'padding',
})<BaseButtonProps>(({ minWidth, padding }) => ({
  ...baseButtonStyles,
  minHeight: '44px',
  minWidth: minWidth || '140px',
  ...(padding && { padding }),
}));

// FormButton - For form submissions and form actions
export const FormButton = styled(Button, {
  shouldForwardProp: (prop) =>
    prop !== 'minWidth' &&
    prop !== 'isActive' &&
    ![
      'accessibilityMode',
      'accessibilityHoverDisabled',
      'accessibilityHoverBackgroundColor',
      'accessibilityHoverColor',
      'accessibilityHoverBorderColor',
    ].includes(prop as string),
})<
  BaseButtonProps &
    ButtonAccessibilityHoverProps & { accessibilityMode?: boolean }
>(({ minWidth, ...accessibilityProps }) => ({
  ...baseButtonStyles,
  minHeight: '50px',
  minWidth: minWidth || '160px',
  padding: '0.5rem 1.5rem',

  '& .MuiButton-startIcon > svg': {
    fontSize: '3rem',
  },

  // Existing hover behaviour preserved exactly; accessibility extras only
  // apply on top when an override prop is explicitly given (see
  // getAccessibilityButtonHoverExtra) - no default visual change.
  '&:hover': {
    ...baseButtonStyles['&:hover'],
    ...getAccessibilityButtonHoverExtra(
      Boolean(accessibilityProps.accessibilityMode),
      accessibilityProps
    ),
  },
}));

// CancelFormButton - Pre-styled cancel button for forms
export const CancelFormButton: React.FC<FormActionButtonProps> = ({
  isSubmitting = false,
  text = 'Cancel',
  onClick,
  disabled,
  ...props
}) => {
  const { accessibilityMode } = useAccessibility();
  return (
    <FormButton
      variant="outlined"
      onClick={onClick}
      disabled={disabled || isSubmitting}
      startIcon={<Cancel />}
      accessibilityMode={accessibilityMode}
      {...props}
      sx={{
        borderColor: colors.primary.main,
        '&:hover': {
          backgroundColor: colors.tertiary.light,
          borderColor: colors.tertiary.dark,
          color: colors.tertiary.dark,
          '& .MuiButton-startIcon > svg': {
            color: colors.tertiary.dark,
          },
        },
        ...props.sx,
      }}
    >
      {text}
    </FormButton>
  );
};

// SaveFormButton - Pre-styled save button for forms
export const SaveFormButton = React.forwardRef<
  HTMLButtonElement,
  FormActionButtonProps
>(
  (
    { isSubmitting = false, text, type = 'submit', disabled, ...props },
    ref
  ) => {
    const buttonText = text || (isSubmitting ? 'Saving...' : 'Save');
    const { accessibilityMode } = useAccessibility();

    return (
      <FormButton
        ref={ref}
        type={type}
        variant="contained"
        disabled={disabled || isSubmitting}
        startIcon={isSubmitting ? <CircularProgress size={20} /> : <Save />}
        accessibilityMode={accessibilityMode}
        {...props}
      >
        {buttonText}
      </FormButton>
    );
  }
);

SaveFormButton.displayName = 'SaveFormButton';

// DeleteFormButton - Pre-styled delete button for forms
export const DeleteFormButton: React.FC<FormActionButtonProps> = ({
  isSubmitting = false,
  text = 'Delete',
  type = 'button',
  disabled,
  ...props
}) => {
  const { accessibilityMode } = useAccessibility();
  return (
    <FormButton
      type={type}
      variant="outlined"
      disabled={disabled || isSubmitting}
      startIcon={isSubmitting ? <CircularProgress size={20} /> : <Delete />}
      accessibilityMode={accessibilityMode}
      sx={{
        color: colors.grey[0],
        borderColor: 'secondary.main',
        backgroundColor: 'secondary.main',
        '&:hover': {
          backgroundColor: 'secondary.main',
          color: '#fff',
          borderColor: 'secondary.main',
          letterSpacing: 'normal',
        },
        ...props.sx,
      }}
      {...props}
    >
      {text}
    </FormButton>
  );
};

// ModalSaveButton - Pre-styled save button for modals (no letter-spacing on hover)
export const ModalSaveButton: React.FC<FormActionButtonProps> = ({
  isSubmitting = false,
  text,
  type = 'submit',
  disabled,
  sx: sxProp,
  ...props
}) => {
  const buttonText = text || (isSubmitting ? 'Saving...' : 'Save');

  return (
    <FormButton
      type={type}
      variant="contained"
      disabled={disabled || isSubmitting}
      startIcon={isSubmitting ? <CircularProgress size={20} /> : <Save />}
      sx={{
        '&:hover': {
          transform: 'none !important',
          boxShadow: 'none',
          letterSpacing: 'normal !important',
        },
        ...sxProp,
      }}
      {...props}
    >
      {buttonText}
    </FormButton>
  );
};

// ModalActionButton - Pre-styled action button for modals
export const ModalActionButton: React.FC<FormActionButtonProps> = ({
  isSubmitting = false,
  text = 'Done',
  type = 'button',
  disabled,
  sx: sxProp,
  ...props
}) => {
  return (
    <FormButton
      type={type}
      variant="contained"
      disabled={disabled || isSubmitting}
      startIcon={<CheckCircle />}
      sx={{
        '&:hover': {
          transform: 'none !important',
          boxShadow: 'none',
          letterSpacing: 'normal !important',
        },
        ...sxProp,
      }}
      {...props}
    >
      {text}
    </FormButton>
  );
};

// ModalCancelButton - Pre-styled cancel button for modals (no letter-spacing on hover)
export const ModalCancelButton: React.FC<FormActionButtonProps> = ({
  isSubmitting = false,
  text = 'Cancel',
  onClick,
  disabled,
  sx: sxProp,
  ...props
}) => {
  return (
    <FormButton
      variant="outlined"
      onClick={onClick}
      disabled={disabled || isSubmitting}
      startIcon={<Cancel />}
      sx={{
        borderColor: colors.primary.main,
        '&:hover': {
          backgroundColor: colors.tertiary.light,
          borderColor: colors.tertiary.dark,
          color: colors.tertiary.dark,
          letterSpacing: 'normal !important',
          '& .MuiButton-startIcon > svg': {
            color: colors.tertiary.dark,
          },
        },
        ...sxProp,
      }}
      {...props}
    >
      {text}
    </FormButton>
  );
};

// ModalDeleteButton - Pre-styled delete button for modals
export const ModalDeleteButton: React.FC<FormActionButtonProps> = ({
  isSubmitting = false,
  text = 'Delete',
  type = 'button',
  disabled,
  onClick,
  sx: sxProp,
  ...props
}) => {
  return (
    <FormButton
      type={type}
      variant="contained"
      color="secondary"
      onClick={onClick}
      disabled={disabled || isSubmitting}
      startIcon={isSubmitting ? <CircularProgress size={20} /> : <Delete />}
      sx={{
        '&:hover': {
          transform: 'none !important',
          boxShadow: 'none',
          letterSpacing: 'normal !important',
        },
        ...sxProp,
      }}
      {...props}
    >
      {text}
    </FormButton>
  );
};

// CloseButton - Pre-styled close button for modals
export const CloseButton: React.FC<FormActionButtonProps> = ({
  isSubmitting = false,
  text = 'Close',
  onClick,
  disabled,
  sx: sxProp,
  ...props
}) => {
  return (
    <FormButton
      variant="outlined"
      onClick={onClick}
      disabled={disabled || isSubmitting}
      startIcon={<Cancel />}
      sx={{
        borderColor: colors.primary.main,
        '&:hover': {
          backgroundColor: '#ffebee',
          borderColor: '#d32f2f',
          color: '#d32f2f',
          letterSpacing: 'normal !important',
          '& .MuiButton-startIcon > svg': {
            color: '#d32f2f',
          },
        },
        ...sxProp,
      }}
      {...props}
    >
      {text}
    </FormButton>
  );
};

// PageButton - For main page actions (like "Add New Client")
export const PageButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'minWidth' && prop !== 'isActive',
})<BaseButtonProps>(({ theme, minWidth }) => ({
  ...baseButtonStyles,
  minHeight: '48px', // Standard page button height
  minWidth: minWidth || '180px',
  padding: '1rem 2rem', // More generous padding for page actions
}));

// TableButton - For buttons used within tables
export const TableButton = styled(Button, {
  shouldForwardProp: (prop) =>
    prop !== 'minWidth' &&
    prop !== 'isActive' &&
    ![
      'accessibilityMode',
      'accessibilityHoverDisabled',
      'accessibilityHoverBackgroundColor',
      'accessibilityHoverColor',
      'accessibilityHoverBorderColor',
    ].includes(prop as string),
})<
  BaseButtonProps &
    ButtonAccessibilityHoverProps & { accessibilityMode?: boolean }
>(({ theme, minWidth, ...accessibilityProps }) => ({
  ...baseButtonStyles,
  minHeight: '36px', // Compact for table rows
  minWidth: minWidth || '120px',
  fontSize: '1.2rem', // Smaller text for tables
  padding: '0.5rem 1rem', // Compact padding

  '&:hover': {
    transform: 'none !important',
    boxShadow: 'none',
    letterSpacing: '1px', // Less expansion in tables
    ...getAccessibilityButtonHoverExtra(
      Boolean(accessibilityProps.accessibilityMode),
      accessibilityProps
    ),
  },
}));

// ============================================================================
// CENTERED FAB POSITIONING CONFIGURATION
// Edit positioning, sizing, and styling values here for all breakpoints
// Breakpoints: mobile (0px), tablet (600px), desktop (900px),
//              large (1200px), xlarge (1536px), xxlarge (2560px)
// ============================================================================
const DEFAULT_FAB_POSITIONING: FABPositioningConfig = {
  mobile: {
    bottom: '3rem',
    right: '2rem',
    minWidth: '72px',
    minHeight: '72px',
    iconSize: '4rem',
    fontSize: '0.7rem',
  },
  tablet: {
    bottom: '3rem',
    right: '3rem',
    minWidth: '128px',
    minHeight: '128px',
    iconSize: '6rem',
    fontSize: '0.85rem',
  },
  desktop: {
    bottom: '4rem',
    right: '4rem',
    minWidth: '128px',
    minHeight: '128px',
    iconSize: '4rem',
    fontSize: '0.9rem',
  },
  large: {
    bottom: '4rem',
    right: '8rem',
    minWidth: '128px',
    minHeight: '128px',
    iconSize: '4.5rem',
    fontSize: '0.9rem',
  },
  xlarge: {
    bottom: '8rem',
    right: '8rem',
    minWidth: '128px',
    minHeight: '128px',
    iconSize: '5rem',
    fontSize: '1rem',
  },
  xxlarge: {
    bottom: '12rem',
    right: '12rem',
    minWidth: '128px',
    minHeight: '128px',
    iconSize: '5rem',
    fontSize: '1rem',
  },
};
// ============================================================================
// END FAB POSITIONING CONFIGURATION
// ============================================================================

// Create the styled component outside with proper prop handling
const StyledFabComponent = styled(Fab, {
  shouldForwardProp: (prop) =>
    prop !== 'positionRelative' &&
    prop !== 'fabPositioning' &&
    prop !== 'hoverText' &&
    prop !== 'showIconByDefault' &&
    prop !== 'hoverIcon' &&
    prop !== 'icon' &&
    prop !== 'iconScale',
})<StyledFabProps>(({
  theme,
  positionRelative = false,
  position = 'fixed',
  bottom = '20px',
  right = '20px',
  top,
  left,
  fabPositioning = DEFAULT_FAB_POSITIONING,
  iconScale = 1,
}) => {
  // Base styles that apply to both positioning modes
  const baseStyles = {
    backgroundColor: colors.secondary.main,
    color: 'white',
    fontSize: '0.9rem',
    fontWeight: 700,
    padding: '36px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    zIndex: 1000,
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
    transition: 'all 0.3s ease',

    // Container for icon and text
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column' as const,

    // Add unique class to isolate FAB styles
    '&.custom-styled-fab': {
      // Default content styling
      '& .fab-default-content': {
        transition: 'opacity 0.3s ease',
        opacity: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },

      // Icon styling - ONLY within custom-styled-fab
      '& .fab-icon': {
        fontSize: '2.25rem',
        width: '2.25rem',
        height: '2.25rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transform: `scale(${iconScale})`,

        // Target only direct child icons
        '& > *': {
          fontSize: '2.25rem !important',
          width: '2.25rem !important',
          height: '2.25rem !important',
        },
      },

      // Text styling
      '& .fab-text': {
        fontSize: '0.9rem',
        fontWeight: 700,
        letterSpacing: '0.5px',
      },

      // Hover content styling
      '& .fab-hover-content': {
        position: 'absolute' as const,
        transition: 'opacity 0.3s ease',
        opacity: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
    },

    '&:hover': {
      backgroundColor: colors.secondary.dark,
      transform: 'scale(1.05)',
      boxShadow: '0 6px 12px rgba(0, 0, 0, 0.4)',

      // Switch content on hover - ONLY within custom-styled-fab
      '&.custom-styled-fab .fab-default-content': {
        opacity: 0,
      },

      '&.custom-styled-fab .fab-hover-content': {
        opacity: 1,
      },
    },

    '&:active': {
      transform: 'scale(0.95)',
    },

    '&:focus': {
      outline: `3px solid ${colors.accessibility.focusOutline || '#f39c12'}`,
      outlineOffset: '2px',
    },

    '&.MuiButtonBase-root': {
      backgroundColor: colors.secondary.main,
      '&:hover': {
        backgroundColor: colors.secondary.dark,
      },
    },
  };

  // Conditional positioning styles
  const positioningStyles = positionRelative
    ? {
        // RELATIVE POSITIONING MODE
        position: 'relative' as const,
        margin: '1rem',
        minWidth: '128px',
        minHeight: '128px',
      }
    : {
        // FIXED POSITIONING MODE (original behavior)
        position: position,
        top: top,
        left: left,
        bottom: bottom,
        right: right,
        minWidth: '128px',
        minHeight: '128px',
      };

  // Generate responsive styles using MediaQueryUtils
  const responsiveStyles = generateFABPositioning(fabPositioning);

  return {
    ...baseStyles,
    ...positioningStyles,
    ...responsiveStyles,
  };
});

// Enhanced Styled Floating Action Button with CSS isolation and position control
export const StyledFab: React.FC<StyledFabProps> = ({
  position = 'fixed',
  bottom = '20px',
  right = '20px',
  top,
  left,
  icon,
  hoverText,
  hoverIcon,
  showIconByDefault = true,
  fabPositioning,
  positionRelative = false,
  iconScale = 1,
  children,
  onClick,
  ...restProps
}) => {
  const domProps = Object.fromEntries(
    Object.entries(restProps).filter(
      ([key]) =>
        ![
          'hoverIcon',
          'hoverText',
          'showIconByDefault',
          'icon',
          'fabPositioning',
          'positionRelative',
          'iconScale',
        ].includes(key)
    )
  );

  // Determine what to show by default and on hover
  const defaultContent = showIconByDefault ? (
    <span className="fab-icon">{icon}</span>
  ) : (
    <span className="fab-text">{children || hoverText || 'FAB'}</span>
  );

  // FIXED: Proper priority - hoverIcon first, then hoverText, then default behavior
  const hoverContent = hoverIcon ? (
    <span className="fab-icon">{hoverIcon}</span>
  ) : hoverText ? (
    <span className="fab-text">{hoverText}</span>
  ) : showIconByDefault ? (
    <span className="fab-text">ADD</span>
  ) : (
    <span className="fab-icon">{icon}</span>
  );

  return (
    <StyledFabComponent
      onClick={onClick}
      className="custom-styled-fab"
      position={position}
      bottom={bottom}
      right={right}
      top={top}
      left={left}
      fabPositioning={fabPositioning}
      positionRelative={positionRelative}
      iconScale={iconScale}
      {...domProps}
    >
      <div className="fab-default-content">{defaultContent}</div>
      <div className="fab-hover-content">{hoverContent}</div>
    </StyledFabComponent>
  );
};

// Enhanced BackButton component using BeingsPageSubMenuButton as base
export const BackButton: React.FC<BackButtonProps> = ({
  navigationMode = 'browser',
  customPath,
  steps = -1,
  onClick,
  'aria-label': ariaLabel = 'Go back',
  ...props
}) => {
  const navigate = useNavigate();

  const handleClick = () => {
    // If custom onClick is provided, use it instead of built-in navigation
    if (onClick) {
      onClick();
      return;
    }

    // Handle navigation based on mode
    switch (navigationMode) {
      case 'custom':
        if (customPath) {
          navigate(customPath);
        } else {
          console.warn(
            'BackButton: customPath is required when navigationMode is "custom"'
          );
          navigate(steps);
        }
        break;
      case 'browser':
      default:
        navigate(steps);
        break;
    }
  };

  return (
    <BeingsPageSubMenuButton
      onClick={handleClick}
      aria-label={ariaLabel}
      sx={{
        minWidth: '68px',
        alignSelf: 'stretch',
        '&:hover': {
          letterSpacing: 'normal',
        },
      }}
      {...props}
    >
      <ArrowBackIcon sx={{ fontSize: '2.5rem' }} />
    </BeingsPageSubMenuButton>
  );
};

// Utility function to create custom button variants
export const createCustomButton = (customStyles: any) => {
  return styled(Button, {
    shouldForwardProp: (prop) => prop !== 'minWidth' && prop !== 'isActive',
  })<BaseButtonProps>(({ theme, minWidth }) => ({
    ...baseButtonStyles,
    minWidth: minWidth || '140px',
    ...customStyles,
  }));
};

// Pre-configured button variants with common color schemes
export const PrimaryButton = styled(BeingMenuButton)<BaseButtonProps>`
  background-color: ${colors.primary.main};
  color: white;
  border-color: ${colors.primary.main};

  &:hover {
    background-color: ${colors.primary.dark};
    border-color: ${colors.primary.dark};
    z-index: 1;
  }
`;

export const SecondaryButton = styled(BeingMenuButton)<BaseButtonProps>`
  background-color: ${colors.secondary.main};
  color: white;
  border-color: ${colors.secondary.main};

  &:hover {
    background-color: ${colors.secondary.dark};
    border-color: ${colors.secondary.dark};
    z-index: 1;
  }
`;

export const TertiaryButton = styled(BeingMenuButton)<BaseButtonProps>`
  background-color: ${colors.tertiary.main};
  color: white;

  border-color: ${colors.tertiary.main};

  &:hover {
    background-color: ${colors.tertiary.dark};
    border-color: ${colors.tertiary.dark};
    z-index: 1;
  }
`;

// BEINGSLIST ACTION BUTTONS
export const BeingsListPrimaryButton = styled(BeingMenuButton)<BaseButtonProps>`
  border-color: ${({ theme }) =>
    theme.palette.mode === 'dark' ? colors.primary.dark : colors.grey[100]};
  color: ${colors.text.light};
  background-color: ${colors.primary.main};

  &:hover {
    background-color: ${colors.primary.dark};
    border-color: ${({ theme }) =>
      theme.palette.mode === 'dark' ? colors.primary.dark : colors.grey[100]};
  }
`;

export const BeingsListSecondaryButton = styled(
  BeingMenuButton
)<BaseButtonProps>`
  border-color: ${({ theme }) =>
    theme.palette.mode === 'dark' ? colors.primary.dark : colors.grey[100]};
  color: ${colors.text.light};
  background-color: ${colors.secondary.main};

  &:hover {
    background-color: ${colors.secondary.dark};
    border-color: ${({ theme }) =>
      theme.palette.mode === 'dark' ? colors.primary.dark : colors.grey[100]};
  }
`;
export const BeingsListTertiaryButton = styled(
  BeingMenuButton
)<BaseButtonProps>`
  border-color: ${({ theme }) =>
    theme.palette.mode === 'dark' ? colors.primary.dark : colors.grey[100]};
  color: ${colors.text.light};
  background-color: ${colors.tertiary.main};

  &:hover {
    background-color: ${colors.tertiary.dark};
    border-color: ${({ theme }) =>
      theme.palette.mode === 'dark' ? colors.primary.dark : colors.grey[100]};
  }
`;

export const OutlinedPrimaryButton = styled(BeingMenuButton)<BaseButtonProps>`
  border-color: ${({ theme }) =>
    theme.palette.mode === 'dark' ? colors.primary.main : colors.grey[100]};
  color: ${colors.text.light};
  background-color: ${colors.primary.main};

  &:hover {
    background-color: ${colors.primary.main + '10'};
    border-color: ${colors.primary.dark};
  }
`;

export const OutlinedSecondaryButton = styled(BeingMenuButton)<BaseButtonProps>`
  border-color: ${colors.secondary.main};
  color: ${colors.secondary.main};
  background-color: transparent;

  &:hover {
    background-color: ${colors.secondary.main + '10'};
    border-color: ${colors.secondary.dark};
  }
`;

export const OutlinedTertiaryButton = styled(BeingMenuButton)<BaseButtonProps>`
  background-color: ${colors.tertiary.main + '80'};
  border-color: ${({ theme }) =>
    theme.palette.mode === 'dark' ? theme.palette.grey[0] : colors.grey[0]};
  color: ${({ theme }) =>
    theme.palette.mode === 'dark' ? theme.palette.grey[0] : colors.grey[0]};

  &:hover {
    background-color: ${colors.tertiary.dark};
    border-color: ${colors.grey[0]};
  }

  ${({ isActive }) =>
    isActive &&
    `
    background-color: ${colors.secondary.main} !important;
    color: ${colors.text?.light || '#ffffff'} !important;
    border-color: ${colors.secondary.main} !important;
  `}
`;

export const OutlinedLightButton = styled(BeingMenuButton)<BaseButtonProps>`
  border-color: ${colors.grey[0]};
  color: ${colors.grey[0]};
  background-color: transparent;

  ${({ isActive }) =>
    isActive &&
    `
    background-color: ${colors.secondary.main} !important;
    color: ${colors.text?.light || '#ffffff'} !important;
    border-color: ${colors.secondary.main} !important;
  `}

  &:hover {
    box-shadow: 0 0 0 4px ${colors.tertiary.light};
    background-color: ${({ isActive }) =>
      isActive ? colors.secondary.dark : colors.tertiary.main + '10'};
    border-color: ${({ isActive }) =>
      isActive ? colors.secondary.dark : colors.grey[0]};
    z-index: 1;
  }
`;

export const TodayButton = styled(BeingMenuButton)<BaseButtonProps>(
  ({ theme }) => ({
    backgroundColor:
      theme.palette.mode === 'dark'
        ? theme.palette.grey[900]
        : colors.primary.main,
    color: theme.palette.grey[50],
    borderColor: theme.palette.grey[50],
    fontSize: '1.2rem',
    padding: '0.01rem 1rem',
    borderRadius: '3px',

    '&:hover': {
      fontWeight: 600,
      backgroundColor: theme.palette.secondary.main,
      color: theme.palette.grey[50],
      borderColor: theme.palette.grey[50],
      boxShadow: `0 0 0 3px ${colors.tertiary.light}`,
      transition: 'all 0.2s ease',
    },
  })
);

export const ResetButton = styled(Box)(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'dark'
      ? theme.palette.grey[900]
      : colors.primary.main,
  color: theme.palette.grey[50],
  border: `3px solid ${theme.palette.grey[0]}`,
  padding: '0.1rem',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  marginLeft: '1rem',
  transition: 'all 0.2s ease',

  '&:hover': {
    transform: 'scale(1.3)',
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.grey[50],
    borderColor: theme.palette.grey[50],
    boxShadow: `0 0 0 3px ${alpha(colors.grey[0], 0.45)}`,
  },
}));

// AccountActionButton - Compact outlined button for account card actions
interface AccountActionButtonProps extends BaseButtonProps {
  accentColor?: string;
  bgColor?: string;
  textColor?: string;
  hoverBgColor?: string;
  hoverTextColor?: string;
}

export const AccountActionButton = styled(BeingMenuButton, {
  shouldForwardProp: (prop) =>
    prop !== 'minWidth' &&
    prop !== 'isActive' &&
    prop !== 'padding' &&
    prop !== 'accentColor' &&
    prop !== 'bgColor' &&
    prop !== 'textColor' &&
    prop !== 'hoverBgColor' &&
    prop !== 'hoverTextColor',
})<AccountActionButtonProps>(({
  theme,
  accentColor,
  minWidth,
  bgColor,
  textColor,
  hoverBgColor,
  hoverTextColor,
}) => {
  const accent = accentColor || theme.palette.primary.main;
  return {
    backgroundColor: bgColor || 'transparent',
    color: textColor || accent,
    borderColor: accent,
    fontSize: '1.5rem',
    padding: '0.01rem 1rem',
    borderRadius: '2px',
    minWidth: minWidth || 'auto',
    minHeight: '38px',

    '@media (max-width: 600px)': {
      minWidth: 'unset',
      width: '100%',
      padding: '0.3rem 1rem',
    },

    '&:hover': {
      backgroundColor: hoverBgColor || accent,
      color: hoverTextColor || '#fff',
      borderColor: accent,
      boxShadow: `0 0 0 3px ${alpha(accent, 0.25)}`,
      transition: 'all 0.2s ease',
    },

    '& .MuiButton-startIcon svg': {
      fontSize: '2rem',
    },

    '&.Mui-disabled': {
      borderColor: theme.palette.action.disabled,
      color: theme.palette.action.disabled,
    },
  };
});

interface SubMenuButtonProps extends BaseButtonProps {
  showBottomBorder?: boolean;
}

export const BeingsPageSubMenuButton = styled(BeingMenuButton, {
  shouldForwardProp: (prop) =>
    prop !== 'minWidth' &&
    prop !== 'isActive' &&
    prop !== 'padding' &&
    prop !== 'showBottomBorder',
})<SubMenuButtonProps>(({ theme, isActive, showBottomBorder }) => ({
  // borderColor: colors.primary.dark,
  borderColor:
    theme.palette.mode === 'dark' ? colors.primary.dark : colors.primary.dark,
  color: theme.palette.mode === 'dark' ? colors.grey[50] : colors.tertiary.dark,
  backgroundColor:
    theme.palette.mode === 'dark' ? colors.primary.dark : colors.primary.light,
  borderTop: 'none',
  borderRight: 'none',
  borderBottom: showBottomBorder
    ? theme.palette.mode === 'dark'
      ? `2px solid ${theme.palette.grey[900]}`
      : `2px solid ${colors.secondary.dark}`
    : 'none',
  fontWeight: '700 !important',
  ...(isActive && {
    backgroundColor: colors.secondary.main,
    color: colors.text?.light || '#ffffff',
    borderColor: colors.secondary.main,
    letterSpacing: '3px !important',
  }),
  '&:hover': {
    backgroundColor: isActive ? colors.secondary.main : colors.primary.main,
    color: colors.text.light,
  },
}));

export const StyledIconButton = styled(IconButton, {
  shouldForwardProp: (prop) =>
    ![
      'variant',
      'backgroundColor',
      'color',
      'hoverBackgroundColor',
      'hoverColor',
    ].includes(prop as string),
})<StyledIconButtonProps>(
  ({
    theme,
    variant = 'standard',
    backgroundColor,
    color,
    hoverBackgroundColor,
    hoverColor,
  }) => ({
    width: '3rem',
    height: '3rem',
    ...(backgroundColor && { backgroundColor }),
    ...(color && { '& svg': { color } }),
    '& svg': {
      fontSize: '2rem',
    },
    transition: 'opacity 0.2s ease, background-color 0.3s ease',

    // Flat props take priority over variants
    ...(hoverBackgroundColor || hoverColor
      ? {
          '&:hover': {
            ...(hoverBackgroundColor && {
              backgroundColor: hoverBackgroundColor,
            }),
            ...(hoverColor && { '& svg': { color: hoverColor } }),
          },
        }
      : {
          // Bold variant: primary background + white icon on hover
          ...(variant === 'bold' && {
            '&:hover': {
              backgroundColor: theme.palette.primary.main,
              '& svg': {
                color: '#fff',
              },
            },
          }),

          // Delete variant: secondary background + white icon on hover
          ...(variant === 'delete' && {
            '&:hover': {
              backgroundColor: theme.palette.secondary.main,
              '& svg': {
                color: '#fff',
              },
            },
          }),

          // Standard variant: default MUI behavior (semi-transparent grey on hover)
        }),
  })
);

// AddRowButton - 4rem primary-bg icon button used to append a new
// row in form lists (pets, kennels, surcharges). Renders the Add icon
// internally so callers only wire the handler.
interface AddRowButtonProps extends Omit<
  IconButtonProps,
  'children' | 'color'
> {
  title?: string;
  'aria-label'?: string;
}

const AddRowIconButton = styled(IconButton)(({ theme }) => ({
  width: '4rem',
  height: '4rem',
  backgroundColor: theme.palette.primary.main,
  color: '#fff',
  borderRadius: '50%',
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
  '&.Mui-disabled': {
    backgroundColor: theme.palette.action.disabledBackground,
    color: theme.palette.action.disabled,
  },
  '& svg': {
    fontSize: '3rem',
    color: '#fff',
  },
}));

export const AddRowButton: React.FC<AddRowButtonProps> = ({
  title,
  'aria-label': ariaLabel,
  ...props
}) => (
  <AddRowIconButton title={title} aria-label={ariaLabel ?? title} {...props}>
    <AddIcon />
  </AddRowIconButton>
);

// Export all button types for easy importing
export {
  BeingMenuButton as ActionButton, // Backward compatibility alias
};

// Types export for TypeScript support
export type {
  BaseButtonProps,
  BackButtonProps,
  FormActionButtonProps,
  StyledFabProps,
  FABPositioningConfig,
  AccountActionButtonProps,
  SubMenuButtonProps,
};
